import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check, ChevronLeft, ChevronRight, Heart, RotateCw, Ticket, ZoomIn, ZoomOut } from 'lucide-react';
import { isFavourite, toggleFavourite } from '@/utils/favourites';
import { photoIdFromUrl } from '@/utils/photoLikes';
import { trackEventPass } from '@/utils/eventPassTracking';
import { useGalleryGestures } from '@/hooks/useGalleryGestures';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PRICES } from '@/utils/cart';
import { PassPhotoClass } from '@/utils/eventPassLookup';

type Slide =
  | { kind: 'photo'; ci: number; pi: number; url: string; n: number }
  | { kind: 'handover'; ci: number }
  | { kind: 'end' };

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  riderName: string;
  classes: PassPhotoClass[];
  start?: { ci: number; pi: number };
  inCart: boolean;
  onAdd: () => void;
  eventId?: string;
  eventName?: string;
  source?: string;
}

const chf = (n: number) => `CHF ${n}.-`;

export const EventPassGallery = ({ open, onOpenChange, riderName, classes, start, inCart, onAdd, eventId = '', eventName = '', source = 'event_ticket' }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const shown = classes.filter(c => c.urls.length > 0);
  const total = shown.reduce((s, c) => s + c.urls.length, 0);
  const fullPrice = classes.length * PRICES.COLLECTION;
  const savings = fullPrice - PRICES.EVENT_PASS;

  const slides = useMemo<Slide[]>(() => {
    const out: Slide[] = [];
    let n = 0;
    shown.forEach((c, ci) => {
      c.urls.forEach((url, pi) => out.push({ kind: 'photo', ci, pi, url, n: ++n }));
      out.push(ci < shown.length - 1 ? { kind: 'handover', ci } : { kind: 'end' });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classes]);

  const [idx, setIdx] = useState(0);
  const railRef = useRef<HTMLDivElement>(null);
  const touchX = useRef<number | null>(null);

  useEffect(() => {
    if (!open) return;
    const ci = start ? shown.findIndex(c => c.participantId === classes[start.ci]?.participantId) : 0;
    const i = slides.findIndex(s => s.kind === 'photo' && s.ci === Math.max(0, ci) && s.pi === (start?.pi ?? 0));
    setIdx(Math.max(0, i));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const slide = slides[idx];
  const curCi = slide?.kind === 'photo' || slide?.kind === 'handover' ? slide.ci : shown.length - 1;
  const reducedMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const g = useGalleryGestures({
    index: idx, count: slides.length,
    onPrev: () => setIdx(i => Math.max(0, i - 1)),
    onNext: () => setIdx(i => Math.min(slides.length - 1, i + 1)),
    zoomEnabled: slide?.kind === 'photo',
    reducedMotion,
  });
  const go = (d: number) => g.slide(d > 0 ? 1 : -1);
  const jumpClass = (ci: number) => setIdx(slides.findIndex(s => s.kind === 'photo' && s.ci === ci));

  // ---- Analytics ----
  const base = { event_id: eventId, source, classes: shown.length, photos: total };
  const seen = useRef(new Set<number>());
  const maxN = useRef(0);
  const openedAt = useRef(0);
  useEffect(() => {
    if (open) { seen.current = new Set(); maxN.current = 0; openedAt.current = Date.now(); return; }
    if (openedAt.current) {
      trackEventPass('gallery_closed', { ...base, photos_seen: seen.current.size, max_progress_pct: total ? Math.round((maxN.current / total) * 100) : 0, seconds: Math.round((Date.now() - openedAt.current) / 1000) });
      openedAt.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    if (!open || !slide) return;
    if (slide.kind === 'photo' && !seen.current.has(slide.n)) {
      seen.current.add(slide.n);
      maxN.current = Math.max(maxN.current, slide.n);
      trackEventPass('photo_viewed', { ...base, photo_number: slide.n, class_number: slide.ci + 1 });
    } else if (slide.kind === 'handover') trackEventPass('class_handover_shown', { ...base, class_number: slide.ci + 1 });
    else if (slide.kind === 'end') trackEventPass('gallery_completed', { ...base, photos_seen: seen.current.size });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, open]);

  // ---- Likes ----
  const [likeTick, setLikeTick] = useState(0);
  useEffect(() => {
    const h = () => setLikeTick(x => x + 1);
    window.addEventListener('favouritesUpdated', h);
    return () => window.removeEventListener('favouritesUpdated', h);
  }, []);
  const curPhotoId = slide?.kind === 'photo' ? photoIdFromUrl(slide.url) : null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const liked = useMemo(() => curPhotoId != null && isFavourite(curPhotoId, eventId), [curPhotoId, eventId, likeTick]);
  const toggleLike = () => {
    if (slide?.kind !== 'photo' || curPhotoId == null) return;
    const c = shown[slide.ci];
    const now = toggleFavourite({
      photoId: curPhotoId, participantId: c.participantId, eventId, raceId: '', eventName,
      raceCategory: c.raceCategory, riderName, horseName: c.horseName, photoUrl: slide.url,
    });
    trackEventPass('photo_liked', { ...base, action: now ? 'liked' : 'unliked', photo_number: slide.n, class_number: slide.ci + 1 });
  };

  useEffect(() => {
    railRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  }, [idx]);


  const AddButton = ({ size = 'default' as 'default' | 'lg' }) => inCart ? (
    <Button size={size} variant="secondary" onClick={() => { trackEventPass('go_to_cart_clicked', { ...base, source: 'gallery' }); onOpenChange(false); navigate('/cart'); }} className="enabled:active:scale-[0.96]">
      <Check /> {t('eventPass.goToCart')}
    </Button>
  ) : (
    <Button size={size} variant="secondary" onClick={onAdd} className="enabled:active:scale-[0.96]">
      <Ticket /> {t('eventPass.add')}
    </Button>
  );

  const ctrlBtn = 'inline-flex h-11 w-11 items-center justify-center rounded-md text-primary-foreground transition-[background-color,scale] duration-150 [-webkit-tap-highlight-color:transparent] [@media(hover:hover)]:hover:bg-primary-foreground/10 active:scale-[0.96] outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground';

  const renderSlide = (sl: Slide, cur: boolean) => {
    if (sl.kind === 'photo') return (
      <img
        src={sl.url}
        alt={cur ? `${riderName} · ${shown[sl.ci].raceCategory} · ${sl.pi + 1}` : ''}
        draggable={false}
        onClick={cur ? g.onImageClick : undefined}
        className="max-h-full max-w-full select-none rounded-md object-contain outline outline-1 -outline-offset-1 outline-[oklch(1_0_0/0.1)]"
        style={cur ? {
          transform: `translate3d(${g.panX}px, ${g.panY}px, 0) scale(${g.scale}) rotate(${g.rotation}deg)`,
          transition: reducedMotion ? 'none' : 'transform 150ms ease-out',
          cursor: g.isZoomed ? 'grab' : 'zoom-in',
        } : undefined}
      />
    );
    if (sl.kind === 'handover') {
      const nx = shown[sl.ci + 1];
      if (!nx) return null;
      return (
        <div className="flex max-w-md flex-col items-center gap-5 text-center">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary-foreground/10"><Check className="h-5 w-5" /></span>
          <p className="text-sm uppercase tracking-widest text-primary-foreground/70">
            {t('eventPass.classDone', { c: sl.ci + 1, classes: shown.length })}
          </p>
          <h3 className="font-serif text-3xl italic text-primary-foreground [text-wrap:balance]">
            {t('eventPass.nextClass', { cls: nx.raceCategory, horse: nx.horseName })}
          </h3>
          <div className="flex gap-2">
            {nx.urls.slice(0, 3).map(u => (
              <img key={u} src={u} alt="" draggable={false} className="h-16 w-24 rounded-md object-cover outline outline-1 -outline-offset-1 outline-[oklch(1_0_0/0.1)]" />
            ))}
          </div>
          <Button size="lg" variant="secondary" onClick={() => { trackEventPass('class_continued', { ...base, class_number: sl.ci + 2 }); go(1); }} tabIndex={cur ? 0 : -1} className="enabled:active:scale-[0.96]">
            {t('eventPass.continueWith', { count: nx.urls.length })} <ArrowRight />
          </Button>
        </div>
      );
    }
    return (
      <div className="flex max-w-md flex-col items-center gap-4 text-center">
        <h3 className="font-serif text-3xl italic text-primary-foreground [text-wrap:balance]">{t('eventPass.seenAll', { count: total })}</h3>
        <p className="text-primary-foreground/75 [text-wrap:pretty]">{t('eventPass.seenAllSub', { classes: shown.length })}</p>
        <div className="tabular-nums">
          <p className="text-sm text-primary-foreground/60 line-through">{t('eventPass.insteadOf', { price: fullPrice })}</p>
          <p className="font-serif text-5xl leading-none">{chf(PRICES.EVENT_PASS)}</p>
          {savings > 0 && <p className="mt-2 text-sm font-semibold">{t('eventPass.save', { savings })}</p>}
        </div>
        <AddButton size="lg" />
        <p className="text-xs text-primary-foreground/60">{t('eventPass.reassurance')}</p>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onKeyDown={g.onKeyDown}
        onContextMenu={e => e.preventDefault()}
        className="flex h-[100dvh] max-h-none w-screen max-w-none flex-col gap-0 rounded-none border-0 bg-primary p-0 text-primary-foreground sm:rounded-none [&>button]:right-4 [&>button]:top-4 [&>button]:h-11 [&>button]:w-11 [&>button]:inline-flex [&>button]:items-center [&>button]:justify-center"
      >
        {/* Header */}
        <div className="flex flex-col gap-3 pb-3 pt-4 pl-4 pr-16 md:pl-8 md:pr-20">
          <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <DialogTitle className="font-serif text-2xl italic text-primary-foreground">{riderName}</DialogTitle>
            <DialogDescription className="text-sm tabular-nums text-primary-foreground/70">
              {slide?.kind === 'photo'
                ? t('eventPass.progress', { n: slide.n, total, c: slide.ci + 1, classes: shown.length })
                : t('eventPass.statsLine', { photos: total, classes: shown.length })}
            </DialogDescription>
          </div>
          {slide?.kind === 'photo' && (
            <div className="-mr-2 flex shrink-0 items-center">
              <button type="button" aria-pressed={liked} aria-label={liked ? t('eventPass.unlike') : t('eventPass.like')} onClick={toggleLike} className={cn(ctrlBtn, 'relative')}>
                <Heart aria-hidden className={cn('h-5 w-5 transition-[opacity,scale,filter] duration-300 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-opacity', liked ? 'scale-[0.25] opacity-0 blur-[4px] motion-reduce:scale-100 motion-reduce:blur-0' : 'scale-100 opacity-100 blur-0')} />
                <Heart aria-hidden className={cn('absolute h-5 w-5 fill-current transition-[opacity,scale,filter] duration-300 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-opacity', liked ? 'scale-100 opacity-100 blur-0' : 'scale-[0.25] opacity-0 blur-[4px] motion-reduce:scale-100 motion-reduce:blur-0')} />
              </button>
              <button type="button" aria-label={g.isZoomed ? t('eventPass.resetZoom') : t('eventPass.zoomIn')} title={g.isZoomed ? t('eventPass.resetZoom') + ' (0)' : t('eventPass.zoomIn') + ' (+)'}
                onClick={() => { trackEventPass('zoom', { ...base, action: g.isZoomed ? 'reset' : 'in' }); g.isZoomed ? g.resetZoom() : g.zoomAt(2); }} className={ctrlBtn}>
                {g.isZoomed ? <ZoomOut className="h-5 w-5" /> : <ZoomIn className="h-5 w-5" />}
              </button>
              <button type="button" aria-label={t('eventPass.rotate')} title={t('eventPass.rotate') + ' (R)'} onClick={() => { trackEventPass('rotate', base); g.rotate(); }} className={ctrlBtn}>
                <RotateCw className="h-5 w-5" />
              </button>
            </div>
          )}
          </div>
          <div role="tablist" className="-mx-1 flex gap-1 overflow-x-auto">
            {shown.map((c, ci) => (
              <button
                key={c.participantId}
                role="tab"
                aria-selected={ci === curCi}
                onClick={() => jumpClass(ci)}
                className={cn(
                  'shrink-0 rounded-md px-3 py-2 text-sm tabular-nums transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground',
                  ci === curCi ? 'bg-primary-foreground text-primary' : 'text-primary-foreground/75 hover:bg-primary-foreground/10',
                )}
              >
                <span className="font-semibold">{c.raceCategory}</span> · {t('eventPass.photosCount', { count: c.urls.length })}
              </button>
            ))}
          </div>
        </div>

        {/* Stage: three-slide rail, same physics as the standard gallery */}
        <div
          ref={g.viewportRef}
          className="relative min-h-0 flex-1 select-none overflow-hidden overscroll-contain"
          style={{ touchAction: g.isZoomed ? 'none' : 'pan-y' }}
        >
          {[-1, 0, 1].map(off => {
            const s2 = slides[idx + off];
            if (!s2) return null;
            const cur = off === 0;
            return (
              <div
                key={idx + off}
                aria-hidden={!cur}
                className={cn('absolute inset-0 flex items-center justify-center px-4 md:px-20', !cur && 'pointer-events-none')}
                style={{ transform: `translate3d(${g.dragX + off * g.width()}px,0,0)`, transition: g.railTransition, willChange: 'transform' }}
              >
                {renderSlide(s2, cur)}
              </div>
            );
          })}

          <button
            type="button" aria-label={t('eventPass.prev')} onClick={() => go(-1)} disabled={idx === 0}
            className="absolute left-2 top-1/2 z-10 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-primary/60 text-primary-foreground backdrop-blur-sm transition-[background-color,opacity] duration-150 [@media(hover:hover)]:hover:bg-primary/80 enabled:active:scale-[0.96] disabled:opacity-30 [-webkit-tap-highlight-color:transparent]"
          ><ChevronLeft className="h-6 w-6" /></button>
          <button
            type="button" aria-label={t('eventPass.next')} onClick={() => go(1)} disabled={idx === slides.length - 1}
            className="absolute right-2 top-1/2 z-10 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-primary/60 text-primary-foreground backdrop-blur-sm transition-[background-color,opacity] duration-150 [@media(hover:hover)]:hover:bg-primary/80 enabled:active:scale-[0.96] disabled:opacity-30 [-webkit-tap-highlight-color:transparent]"
          ><ChevronRight className="h-6 w-6" /></button>
        </div>

        {/* Thumbnail rail */}
        <div ref={railRef} className="flex gap-1.5 overflow-x-auto overscroll-contain px-4 py-3 md:px-8">
          {slides.map((s, i) => s.kind === 'photo' ? (
            <button
              key={i}
              data-active={i === idx}
              aria-label={`${shown[s.ci].raceCategory} ${s.pi + 1}`}
              onClick={() => setIdx(i)}
              className={cn('h-12 w-16 shrink-0 overflow-hidden rounded-md transition-[opacity,filter] duration-150', i === idx ? 'opacity-100 ring-2 ring-primary-foreground' : s.ci === curCi ? 'opacity-60 hover:opacity-90' : 'opacity-30 grayscale hover:opacity-60')}
            >
              <img src={s.url} alt="" draggable={false} className="h-full w-full object-cover" />
            </button>
          ) : (
            s.kind === 'handover' && shown[s.ci + 1] ? (
              <button
                key={i}
                type="button"
                onClick={() => jumpClass(s.ci + 1)}
                className="flex h-12 shrink-0 items-center gap-1.5 rounded-md px-3 text-xs text-primary-foreground/50 transition-colors duration-150 hover:bg-primary-foreground/10 hover:text-primary-foreground/80"
              >
                <span className="whitespace-nowrap"><span className="font-semibold">{shown[s.ci + 1].raceCategory}</span> · {shown[s.ci + 1].horseName}</span>
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            ) : null
          ))}
        </div>

        {/* Purchase bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 bg-primary-container px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:px-8">
          <div className="tabular-nums">
            <p className="text-sm font-semibold">{t('eventPass.barTitle', { count: total })}</p>
            <p className="text-sm text-primary-foreground/75">
              {chf(PRICES.EVENT_PASS)} <span className="line-through opacity-70">{t('eventPass.insteadOf', { price: fullPrice })}</span>
            </p>
          </div>
          <AddButton />
        </div>
      </DialogContent>
    </Dialog>
  );
};
